import { n as DEFAULT_ARCHIVE_LIMITS } from "../archive-limits-CMo2zcn-.js";
import { n as listZipEntriesCapped, r as readZipEntryBlob, t as DecompressionBudget } from "../capped-zip-entries-CIyZmVN5.js";
import { LocalCacheByteSource } from "./local-cache-byte-source.js";
import { ZipPackagingError, assertSafeNewZipPaths, assertWritableZipData, writeStoreZip } from "./pack-files-as-zip.js";
import { ByteSourceReader } from "./zip-byte-source-reader.js";
import { ZipReader } from "@zip.js/zip.js";
//#region ../src/storage/zip-rebuild.ts
/**
* `rebuildZipWithEntries` - a new zip from an existing one plus entries
* added or replaced by path. The Tour Viewer's finish step writes the
* measured code (`qr/<id>.json`), the content manifest (`tour.json`) and
* the captured photos into the zip the creator hosted; the coverage embed
* (`zip-coverage-embed.ts`) replaces `session.json` through the same loop.
*
* It is a pure transform `(zip, entries) -> zip`: every entry of the input
* is read and re-emitted uncompressed through the store-mode writer, so
* untouched entries stay byte-identical and the archive stays range-
* readable. Unlike the coverage embed's wrapper it NEVER returns the input
* on failure - it throws. The caller here is a creator about to upload the
* result over the hosted file, and an old zip handed back as "rebuilt"
* would lose the measuring session silently.
*
* Two rules from the M1 review of the guided-setup plan:
* - Carried entries are read into BLOBS, not `Uint8Array`s: a browser keeps
*   Blobs off the JS heap, so the peak cost is the output archive, not the
*   input twice over (review #1; a recorder zip is hundreds of photos).
* - An archive that opened is re-emitted as it is, whatever its entry
*   names (review #2): a duplicate name or a `./` segment in a hosted zip
*   is not the creator's fault, and a rebuild that refused it would
*   dead-end the setup. Duplicate names in the input collapse to the LAST
*   occurrence, which is what readers resolve.
* - New entries are validated, but under the ARCHIVE'S OWN convention: in
*   a zip whose entries carry a leading `./`, a new path with that prefix
*   is following the archive rather than inventing a traversal segment, so
*   it is checked with the prefix removed. Everything else about the name
*   is checked as usual, and duplicates are compared on the normalised
*   form so `./x` and `x` cannot both be written.
* - That convention also decides WHERE a new name lands: it joins its
*   neighbours under the prefix when every existing entry carries one, and
*   is otherwise written exactly as the caller spelled it. A mixed archive
*   has no convention to guess at.
*
* The input is UNTRUSTED (K0 milestone review R1): the Tour Viewer's input
* is a tour from any link or a file someone handed the creator, so a
* carried entry can be a deflate bomb. The directory is read through a
* single-read cap, listed under the entry cap, and every carried entry is
* inflated under a `DecompressionBudget` - the open session's own when the
* caller passes it, so a Finish shares the tour's total.
*/
/**
* The prefix some zip tools put on every entry. An archive written that way
* is legal and this module has always re-emitted it; what changed in r665
* is that a CALLER following the same convention for a NEW entry is no
* longer refused.
*/
const DOT_SLASH = "./";
/** Does this archive write ANY entry with a leading `./`? Enough to accept
*  a new path that carries one: the caller derived that prefix from the
*  archive rather than inventing it. */
function archiveUsesDotSlash(archiveNames) {
	return archiveNames.some((name) => name.startsWith(DOT_SLASH));
}
/** Does EVERY entry carry it? A stricter question, and the one that decides
*  whether an unprefixed NEW name is moved under the prefix. A mixed
*  archive has no convention to follow, so nothing is guessed there: the
*  name is written as the caller spelled it. */
function archiveIsAllDotSlash(archiveNames) {
	return archiveNames.length > 0 && archiveNames.every((name) => name.startsWith(DOT_SLASH));
}
/**
* The path as the rules should see it: with the archive's own `./` prefix
* removed, when the archive uses one. Everything else is left alone - this
* is a tolerance for ONE known convention, not a normaliser.
*/
function underArchiveConvention(path, dotSlash) {
	return dotSlash && path.startsWith(DOT_SLASH) ? path.slice(2) : path;
}
/** Two new entries at the same path would be written twice. The shared path
*  checker cannot see this once archive-derived names are filtered out of
*  it, so the rebuild states the rule for itself - and compares the
*  NORMALISED form, because `./session.json` and `session.json` are one
*  file to every reader and would otherwise both be written. */
function assertNoDuplicateNewPaths(entries, dotSlash) {
	const seen = /* @__PURE__ */ new Set();
	for (const entry of entries) {
		const key = underArchiveConvention(entry.path, dotSlash);
		if (seen.has(key)) throw new ZipPackagingError(`rebuildZipWithEntries: entry '${entry.path}' is a duplicate entry path`);
		seen.add(key);
	}
}
/**
* Re-emit `zip` with `entries` added, replacing any existing entry at the
* same path. Directory entries of the input are dropped (paths stay
* implied by file names, as every writer in this package does).
*
* @throws {ZipPackagingError} when `zip` is not a readable archive, when a
*   NEW entry path is unsafe or duplicated or its data is not writable, or
*   when writing fails. Nothing partial is ever returned. A cap the input
*   passes (`ArchiveLimitError`) is its `cause`.
*/
async function rebuildZipWithEntries(zip, entries, options = {}) {
	const reader = new ZipReader(new ByteSourceReader(new LocalCacheByteSource(zip), DEFAULT_ARCHIVE_LIMITS.maxDirectoryBytes));
	const budget = options.budget ?? DecompressionBudget.forArchive(zip.size);
	try {
		const all = lastOccurrences((await listZipEntriesCapped(reader, options.maxEntries)).filter((e) => !e.directory));
		const archiveNameList = all.map((e) => e.filename);
		const dotSlash = archiveUsesDotSlash(archiveNameList);
		const allDotSlash = archiveIsAllDotSlash(archiveNameList);
		const archiveByName = new Map(all.map((e) => [underArchiveConvention(e.filename, dotSlash), e.filename]));
		const targeted = entries.map((e) => {
			const key = underArchiveConvention(e.path, dotSlash);
			const existing = archiveByName.get(key);
			if (existing !== void 0) return {
				...e,
				path: existing
			};
			return {
				...e,
				path: allDotSlash ? DOT_SLASH + key : e.path
			};
		});
		assertSafeNewZipPaths(targeted.filter((e) => !archiveByName.has(underArchiveConvention(e.path, dotSlash))).map((e) => ({
			...e,
			path: underArchiveConvention(e.path, dotSlash)
		})), "rebuildZipWithEntries");
		assertNoDuplicateNewPaths(entries, dotSlash);
		assertWritableZipData(entries, "rebuildZipWithEntries");
		const removed = removedArchiveNames(options.remove ?? [], entries, archiveByName);
		const replaced = new Set(targeted.map((e) => e.path));
		const existing = all.filter((e) => !replaced.has(e.filename) && !removed.has(e.filename));
		const total = existing.length + targeted.length;
		const carried = [];
		for (const entry of existing) {
			carried.push({
				path: entry.filename,
				data: await readZipEntryBlob(entry, budget, "")
			});
			options.onProgress?.(carried.length, total);
		}
		const out = await writeStoreZip([...carried, ...targeted], "rebuildZipWithEntries");
		options.onProgress?.(total, total);
		return out;
	} catch (err) {
		if (err instanceof ZipPackagingError) throw err;
		throw new ZipPackagingError(`rebuildZipWithEntries: reading the archive failed: ${err instanceof Error ? err.message : String(err)}`, { cause: err });
	} finally {
		await reader.close();
	}
}
/**
* The archive's own names for the paths in `remove`; paths the archive
* does not carry drop out. Every name here is compared BARE (without a
* leading `./`): `./x` and `x` name one file to every reader, and
* `archiveByName` is keyed bare already (a flat archive has no `./` names
* to strip). One rule on both sides, so the contradiction check cannot
* miss a pairing (PR #531 review).
*
* @throws {ZipPackagingError} when a removed path is also a new entry.
*/
function removedArchiveNames(remove, entries, archiveByName) {
	const bare = (path) => underArchiveConvention(path, true);
	const written = new Set(entries.map((e) => bare(e.path)));
	const out = /* @__PURE__ */ new Set();
	for (const path of remove) {
		const key = bare(path);
		if (written.has(key)) throw new ZipPackagingError(`rebuildZipWithEntries: entry '${path}' is both removed and written`);
		const existing = archiveByName.get(key);
		if (existing !== void 0) out.add(existing);
	}
	return out;
}
/** Input order, each name once, the LAST occurrence kept. */
function lastOccurrences(entries) {
	const byName = /* @__PURE__ */ new Map();
	for (const entry of entries) byName.set(entry.filename, entry);
	return [...byName.values()];
}
//#endregion
export { rebuildZipWithEntries };
